/**
 * AdminNotificaciones
 * -------------------
 * Dashboard del Centro de Notificaciones · Fase 2.
 *
 * Matriz: filas = eventos del catálogo agrupados por categoría,
 * columnas = canales (in-app, email, WhatsApp) + destinatarios + acción de prueba.
 *
 * Cada celda canal es un toggle. Destinatarios editables vía dialog.
 * Botón "Probar" envía un email demo a la cuenta indicada.
 */
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Bell, Mail, MessageSquare, Send, Users, Loader2, Edit, RotateCcw, Info, Download,
} from "lucide-react";

const SEVERIDAD_COLORS = {
  info: "bg-blue-500/15 text-blue-300 border-blue-500/30",
  warning: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  critical: "bg-red-500/15 text-red-300 border-red-500/30",
};

const CANAL_ICON = {
  inapp: Bell,
  email: Mail,
  whatsapp: MessageSquare,
  telegram: Send,
};
const CANAL_LABEL = {
  inapp: "In-app",
  email: "Email",
  whatsapp: "WhatsApp",
  telegram: "Telegram",
};

export default function AdminNotificaciones() {
  const [loading, setLoading] = useState(true);
  const [categorias, setCategorias] = useState({});
  const [canales, setCanales] = useState(["inapp", "email", "whatsapp", "telegram"]);
  const [eventos, setEventos] = useState([]);
  const [destDialog, setDestDialog] = useState(null); // { evento }
  const [testDialog, setTestDialog] = useState(null); // { evento }
  const [testEmail, setTestEmail] = useState("");
  const [testSending, setTestSending] = useState(false);
  const [roles, setRoles] = useState([]);
  const [users, setUsers] = useState([]);
  const [digestRunning, setDigestRunning] = useState(false);

  const ejecutarDigest = async () => {
    if (!window.confirm("Se enviarán AHORA los 5 emails del digest diario corporativo a sus destinatarios configurados. ¿Continuar?")) return;
    setDigestRunning(true);
    try {
      const r = await api.post("/admin/notificaciones/digest-diario/run");
      const enviados = Object.entries(r.data?.resultados || {})
        .filter(([, v]) => v?.channels?.email?.ok).length;
      toast.success(`Digest enviado · ${enviados}/5 emails entregados · «${r.data?.quote?.frase?.slice(0, 60)}…»`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error ejecutando digest");
    } finally { setDigestRunning(false); }
  };

  const descargarRoadmap = async () => {
    try {
      const r = await api.get("/admin/roadmap.xlsx", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `Roadmap_DNF_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("Roadmap descargado");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error descargando roadmap");
    }
  };

  const reload = async () => {
    try {
      const r = await api.get("/admin/notificaciones/eventos");
      setCategorias(r.data.categorias || {});
      setCanales(r.data.canales || []);
      setEventos(r.data.eventos || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error cargando eventos");
    }
  };

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const r = await api.get("/admin/notificaciones/eventos");
        if (cancel) return;
        setCategorias(r.data.categorias || {});
        setCanales(r.data.canales || []);
        setEventos(r.data.eventos || []);
      } catch (e) {
        if (!cancel) toast.error(e?.response?.data?.detail || "Error cargando eventos");
      } finally {
        if (!cancel) setLoading(false);
      }
      try {
        const r2 = await api.get("/admin/notificaciones/roles");
        if (cancel) return;
        setRoles(r2.data.roles || []);
        setUsers(r2.data.users || []);
      } catch {  /* opcional */ }
    })();
    return () => { cancel = true; };
  }, []);

  const eventosPorCat = useMemo(() => {
    const out = {};
    for (const ev of eventos) {
      const c = ev.categoria || "otros";
      if (!out[c]) out[c] = [];
      out[c].push(ev);
    }
    return out;
  }, [eventos]);

  const ordenCategorias = useMemo(() => {
    return Object.entries(categorias)
      .sort(([, a], [, b]) => (a.orden || 99) - (b.orden || 99))
      .map(([k]) => k);
  }, [categorias]);

  const toggleCanal = async (ev, canal, valor) => {
    const nuevosCanales = { ...(ev.channels || {}), [canal]: valor };
    // Optimistic update
    setEventos((prev) => prev.map((e) => e.key === ev.key
      ? { ...e, channels: nuevosCanales }
      : e));
    try {
      await api.put(`/admin/notificaciones/eventos/${ev.key}`, {
        channels: nuevosCanales,
        destinatarios: ev.destinatarios || {},
      });
      toast.success(`${CANAL_LABEL[canal]} ${valor ? "activado" : "desactivado"} para ${ev.label}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error actualizando");
      reload(); // revert
    }
  };

  const guardarDestinatarios = async (ev, nuevos) => {
    setEventos((prev) => prev.map((e) => e.key === ev.key
      ? { ...e, destinatarios: { ...(e.destinatarios || {}), ...nuevos } }
      : e));
    try {
      await api.put(`/admin/notificaciones/eventos/${ev.key}`, {
        channels: ev.channels || {},
        destinatarios: { ...(ev.destinatarios || {}), ...nuevos },
      });
      toast.success("Destinatarios actualizados");
      setDestDialog(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error guardando");
      reload();
    }
  };

  const enviarTest = async () => {
    if (!testEmail || !testEmail.includes("@")) {
      toast.error("Introduce un email válido");
      return;
    }
    setTestSending(true);
    try {
      const r = await api.post(
        `/admin/notificaciones/eventos/${testDialog.evento.key}/test`,
        { email: testEmail }
      );
      if (r.data?.ok) {
        toast.success(`Email de prueba enviado a ${testEmail}`);
        setTestDialog(null);
      } else {
        toast.error(r.data?.error || "Error en envío");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error en envío");
    } finally { setTestSending(false); }
  };

  return (
    <div className="space-y-6" data-testid="admin-notificaciones-page">
      <PageHeader
        title="Centro de notificaciones"
        subtitle="Configura por evento qué canales se usan (in-app · email · WhatsApp · Telegram) y a quién llegan. Telegram/WhatsApp llegan a los usuarios destinatarios que tengan vinculado el canal en su Perfil."
        action={
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={descargarRoadmap}
              data-testid="btn-download-roadmap"
            >
              <Download className="w-4 h-4 mr-2" />
              Descargar Roadmap (.xlsx)
            </Button>
            <Button
              variant="outline"
              onClick={ejecutarDigest}
              disabled={digestRunning}
              data-testid="btn-run-digest"
            >
              {digestRunning ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
              Ejecutar digest 07:00 ahora
            </Button>
          </div>
        }
      />

      <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-4 text-sm text-amber-200 flex gap-3 items-start">
        <Info className="w-5 h-5 mt-0.5 shrink-0" />
        <div>
          <div className="font-semibold mb-1">Cómo funciona</div>
          <ul className="space-y-1 text-amber-100/80 text-[13px]">
            <li>• Cada usuario puede silenciar canales en su perfil <code className="bg-black/30 px-1 rounded">Perfil → Mis notificaciones</code>.</li>
            <li>• El canal WhatsApp se ha activado con plantillas oficiales de Meta (Jun 2026). Solo dispara para los eventos que tienen una plantilla aprobada vinculada.</li>
            <li>• Algunos eventos resuelven destinatarios automáticamente (el montador asignado, el cliente del PDV, etc.).</li>
          </ul>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-zinc-400">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Cargando catálogo…
        </div>
      ) : ordenCategorias.map((catKey) => {
        const cat = categorias[catKey];
        const evs = eventosPorCat[catKey] || [];
        if (evs.length === 0) return null;
        return (
          <section key={catKey} className="space-y-2">
            <h3 className="text-lg font-semibold flex items-center gap-2 text-zinc-100">
              <span className="text-2xl">{cat.icon}</span>
              <span>{cat.label}</span>
              <Badge variant="outline" className="ml-2 text-zinc-400">{evs.length}</Badge>
            </h3>
            <div className="overflow-x-auto rounded-lg border border-zinc-800 bg-zinc-950/60">
              <table className="w-full text-sm">
                <thead className="bg-zinc-900/80 text-zinc-400 text-xs uppercase tracking-wider">
                  <tr>
                    <th className="px-3 py-2 text-left">Evento</th>
                    {canales.map((c) => {
                      const Icon = CANAL_ICON[c];
                      return (
                        <th key={c} className="px-3 py-2 text-center w-24">
                          <div className="flex flex-col items-center gap-1">
                            <Icon className="w-4 h-4" />
                            <span>{CANAL_LABEL[c]}</span>
                          </div>
                        </th>
                      );
                    })}
                    <th className="px-3 py-2 text-left">Destinatarios</th>
                    <th className="px-3 py-2 text-right w-32">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {evs.map((ev) => (
                    <tr key={ev.key} className="border-t border-zinc-800/60 hover:bg-zinc-900/40">
                      <td className="px-3 py-2 align-top max-w-md">
                        <div className="flex items-start gap-2">
                          <div className="flex-1">
                            <div className="font-medium text-zinc-100 flex items-center gap-2">
                              {ev.label}
                              <Badge className={`text-[10px] ${SEVERIDAD_COLORS[ev.severidad] || ""}`} variant="outline">
                                {ev.severidad}
                              </Badge>
                              {ev.overridden && (
                                <Badge variant="outline" className="text-[10px] text-cyan-300 border-cyan-500/40">custom</Badge>
                              )}
                            </div>
                            <div className="text-xs text-zinc-500 mt-1">{ev.descripcion}</div>
                            <code className="text-[10px] text-zinc-600">{ev.key}</code>
                          </div>
                        </div>
                      </td>
                      {canales.map((c) => (
                        <td key={c} className="px-3 py-2 text-center align-middle">
                          <Switch
                            checked={!!(ev.channels || {})[c]}
                            onCheckedChange={(v) => toggleCanal(ev, c, v)}
                            data-testid={`toggle-${ev.key}-${c}`}
                          />
                        </td>
                      ))}
                      <td className="px-3 py-2 text-xs align-top">
                        <DestinatariosResumen evento={ev} />
                      </td>
                      <td className="px-3 py-2 text-right align-top">
                        <div className="flex justify-end gap-1">
                          <Button
                            size="sm" variant="outline"
                            onClick={() => setDestDialog({ evento: ev })}
                            data-testid={`btn-edit-dest-${ev.key}`}
                          >
                            <Edit className="w-3.5 h-3.5" />
                          </Button>
                          <Button
                            size="sm" variant="outline"
                            onClick={() => { setTestDialog({ evento: ev }); setTestEmail(""); }}
                            data-testid={`btn-test-${ev.key}`}
                          >
                            <Send className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        );
      })}

      {/* ============ DIALOG: destinatarios ============ */}
      <Dialog open={!!destDialog} onOpenChange={(o) => !o && setDestDialog(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Destinatarios · {destDialog?.evento?.label}</DialogTitle>
            <DialogDescription>
              Configura quién recibe las notificaciones de este evento. Puedes mezclar emails directos, roles del sistema y usuarios concretos.
            </DialogDescription>
          </DialogHeader>
          {destDialog && (
            <DestEditor
              evento={destDialog.evento}
              roles={roles}
              users={users}
              onSave={(nuevos) => guardarDestinatarios(destDialog.evento, nuevos)}
              onCancel={() => setDestDialog(null)}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* ============ DIALOG: envío de prueba ============ */}
      <Dialog open={!!testDialog} onOpenChange={(o) => !o && setTestDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Envío de prueba</DialogTitle>
            <DialogDescription>
              Te envía un email con datos dummy del evento <b>{testDialog?.evento?.label}</b> para que veas cómo queda visualmente.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label>Email destinatario</Label>
            <Input
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              placeholder="tu@email.com"
              autoFocus
              data-testid="test-email-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTestDialog(null)}>Cancelar</Button>
            <Button onClick={enviarTest} disabled={testSending} data-testid="btn-confirm-test">
              {testSending ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />}
              Enviar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


function DestinatariosResumen({ evento }) {
  const d = evento.destinatarios || {};
  const parts = [];
  // Emails directos / overridden / default
  let emails = d.emails;
  if (!emails) {
    emails = d.emails_default || "";
  }
  const emailList = (typeof emails === "string" ? emails.split(",") : emails || [])
    .map((e) => e?.trim()).filter((e) => e && e.includes("@"));
  if (emailList.length > 0) {
    parts.push(<div key="emails" className="text-zinc-300 truncate" title={emailList.join(", ")}>
      📧 {emailList.join(", ")}
    </div>);
  }
  if (d.roles?.length > 0) {
    parts.push(<div key="roles" className="text-zinc-400">
      <Users className="w-3 h-3 inline mr-1" /> Roles: {d.roles.join(", ")}
    </div>);
  }
  if (d.user_ids?.length > 0) {
    parts.push(<div key="users" className="text-zinc-400">👤 {d.user_ids.length} usuario(s)</div>);
  }
  if (d.auto_montador) parts.push(<div key="am" className="text-cyan-400">→ montador asignado</div>);
  if (d.auto_cliente) parts.push(<div key="ac" className="text-cyan-400">→ cliente del PDV</div>);
  if (d.auto_asignado) parts.push(<div key="aa" className="text-cyan-400">→ usuario asignado a la tarea</div>);
  if (parts.length === 0) return <span className="text-zinc-600">— sin destinatarios —</span>;
  return <div className="space-y-0.5">{parts}</div>;
}


function DestEditor({ evento, roles, users, onSave, onCancel }) {
  const d = evento.destinatarios || {};
  const initialEmails = typeof d.emails === "string"
    ? d.emails
    : (d.emails || []).join(", ") || (d.emails_default || "");
  const [emails, setEmailsLocal] = useState(initialEmails);
  const [rolesSel, setRolesSel] = useState(d.roles || []);
  const [userIds, setUserIds] = useState(d.user_ids || []);

  const toggleRole = (r) => {
    setRolesSel((prev) => prev.includes(r) ? prev.filter((x) => x !== r) : [...prev, r]);
  };
  const toggleUser = (uid) => {
    setUserIds((prev) => prev.includes(uid) ? prev.filter((x) => x !== uid) : [...prev, uid]);
  };

  const resetDefaults = () => {
    setEmailsLocal(d.emails_default || "");
    setRolesSel([]);
    setUserIds([]);
  };

  return (
    <div className="space-y-4">
      <div className="space-y-1">
        <Label>Emails directos (coma-separados)</Label>
        <Textarea
          value={emails}
          onChange={(e) => setEmailsLocal(e.target.value)}
          placeholder="email1@dnf.com, email2@dnf.com"
          rows={2}
          data-testid="dest-emails-input"
        />
        {d.emails_env && (
          <p className="text-[11px] text-zinc-500">
            Default desde <code className="bg-black/30 px-1 rounded">{d.emails_env}</code>: <span className="text-zinc-400">{d.emails_default || "(vacío)"}</span>
          </p>
        )}
      </div>

      {roles.length > 0 && (
        <div className="space-y-2">
          <Label>Roles del sistema</Label>
          <div className="flex flex-wrap gap-2">
            {roles.map((r) => (
              <Badge
                key={r}
                variant={rolesSel.includes(r) ? "default" : "outline"}
                className="cursor-pointer"
                onClick={() => toggleRole(r)}
                data-testid={`role-${r}`}
              >
                {r}
              </Badge>
            ))}
          </div>
        </div>
      )}

      {users.length > 0 && (
        <div className="space-y-2">
          <Label>Usuarios específicos</Label>
          <div className="max-h-44 overflow-y-auto border border-zinc-800 rounded p-2 space-y-1">
            {users.map((u) => (
              <label key={u.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-zinc-900/60 p-1 rounded">
                <input
                  type="checkbox"
                  checked={userIds.includes(u.id)}
                  onChange={() => toggleUser(u.id)}
                  data-testid={`user-${u.id}`}
                />
                <span className="text-zinc-200">{u.nombre || u.email}</span>
                <span className="text-zinc-500 text-xs">{u.email}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      <div className="flex justify-between pt-2 border-t border-zinc-800">
        <Button variant="ghost" size="sm" onClick={resetDefaults}>
          <RotateCcw className="w-3.5 h-3.5 mr-1" /> Resetear defaults
        </Button>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onCancel}>Cancelar</Button>
          <Button
            onClick={() => onSave({ emails, roles: rolesSel, user_ids: userIds })}
            data-testid="btn-save-dest"
          >Guardar</Button>
        </div>
      </div>
    </div>
  );
}
